import React from 'react';
import { Code, Construction } from '@mui/icons-material';
import { PageContainer, Content } from '../PageTemplate';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const GradientHero = styled.div`
  background: linear-gradient(
    150deg,
    #ff80b5 0%,
    #9089fc 20%,
    #ff80b5 40%,
    #40c9ff 60%,
    #a5fecb 80%,
    #9089fc 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 16rem 2rem 8rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    filter: blur(60px);
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    padding: 12rem 1rem 6rem 1rem;
    
    h1 {
      font-size: 2.5rem !important;
    }
    
    p {
      font-size: 1rem !important;
      padding: 0 1rem;
    }
  }
`;

const DevelopmentBanner = styled.div`
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  text-align: center;
  padding: 0.75rem;
  position: relative;
  overflow: hidden;
  font-weight: 500;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url('/noise.png') repeat;
    opacity: 0.1;
    mix-blend-mode: overlay;
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const MatrixContainer = styled.div`
  width: 100%;
  height: 300px;
  position: relative;
  background: rgba(0, 0, 0, 0.95);
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    height: 200px;
  }
`;

const MatrixCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MatrixText = styled.div`
  font-size: 4rem;
  font-family: monospace;
  text-align: center;
  position: relative;
  z-index: 2;
  font-weight: 900;
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  letter-spacing: 2px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  background: linear-gradient(
    45deg,
    #ff80b5 0%,
    #9089fc 25%,
    #40c9ff 50%,
    #a5fecb 75%,
    #9089fc 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-size: 200% auto;

  @media (max-width: 768px) {
    font-size: 2rem;
    padding: 15px;
    letter-spacing: 1px;
  }

  @media (max-width: 380px) {
    font-size: 1.75rem;
  }
`;

const MatrixRain = () => {
  React.useEffect(() => {
    const canvas = document.getElementById('matrixCanvas');
    const ctx = canvas.getContext('2d');

    const setCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    };

    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*';
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    const drops = new Array(Math.floor(columns)).fill(1);
    
    const colors = [
      '#ff80b5',  // Pink
      '#9089fc',  // Purple
      '#40c9ff',  // Blue
      '#a5fecb'   // Mint
    ];

    function draw() {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)];
        
        const colorIndex = Math.floor(Math.random() * colors.length);
        ctx.fillStyle = colors[colorIndex];
        ctx.font = `${fontSize}px monospace`;
        
        ctx.shadowBlur = 5;
        ctx.shadowColor = colors[colorIndex];
        
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        
        ctx.shadowBlur = 0;

        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    }

    const interval = setInterval(draw, 33);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', setCanvasSize);
    };
  }, []);

  return (
    <MatrixContainer>
      <MatrixCanvas id="matrixCanvas" />
      <MatrixText>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ 
            duration: 2,
            ease: "easeOut"
          }}
        >
          developing...
        </motion.div>
      </MatrixText>
    </MatrixContainer>
  );
};

const ApiDocumentation = () => {
  return (
    <PageContainer>
      <GradientHero>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Code sx={{ 
            fontSize: 64, 
            marginBottom: 2,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
          }} />
          <motion.h1
            style={{
              fontSize: '3.5rem',
              marginBottom: '1.5rem',
              fontWeight: 700,
              position: 'relative',
              zIndex: 2,
              padding: '0 1rem'
            }}
          >
            API Documentation
          </motion.h1>
          <motion.p
            style={{
              fontSize: '1.25rem',
              maxWidth: '800px',
              margin: '0 auto',
              opacity: 0.9,
              lineHeight: 1.6,
              position: 'relative',
              zIndex: 2
            }}
          >
            Developer resources and API documentation
          </motion.p>
        </motion.div>
      </GradientHero>

      <DevelopmentBanner>
        <span>
          <Construction sx={{ fontSize: 20 }} />
          API documentation is under development. Check back soon!
        </span>
      </DevelopmentBanner>
      <MatrixRain />

      <Content>
        {/* Add your content here */}
      </Content>
    </PageContainer>
  );
};

export default ApiDocumentation;